import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Banknote,
  FileSignature,
  Landmark
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import ContractsPage from './ContractsPage';
import PaymentsPanel from './PaymentsPanel';
import CashPage from './CashPage';
import { useAuth } from '../../core/auth/AuthProvider';
import { PERMISSIONS } from '../../core/permissions/roles';

const VIEWS = {
  home: 'home',
  contracts: 'contracts',
  payments: 'payments',
  cash: 'cash'
};

export default function FinancePage() {
  const { can } = useAuth();
  const canGlobalFinance = can(PERMISSIONS.FINANCE_GLOBAL_VIEW);
  const canOwnCash = can(PERMISSIONS.CASH_OWN_VIEW);
  const canContracts = can(PERMISSIONS.FINANCE_CONTRACTS_MANAGE);
  const canStudentPayments = can(PERMISSIONS.FINANCE_STUDENT_LOOKUP);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get('view');
  const [view, setView] = useState(
    Object.values(VIEWS).includes(initialView) ? initialView : VIEWS.home
  );

  const title = useMemo(
    () =>
      ({
        home: 'Finance & caisse',
        contracts: 'Contrats',
        payments: 'Paiements',
        cash: 'Caisse'
      })[view],
    [view]
  );

  function openView(nextView) {
    setView(nextView);

    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    setSearchParams(next);
  }

  function goHome() {
    setView(VIEWS.home);

    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next);
  }

  if (view === VIEWS.contracts && canContracts) {
    return (
      <>
        <button type="button" className="finance-back-button" onClick={goHome}>
          <ArrowLeft size={17} />
          Retour à Finance & caisse
        </button>
        <ContractsPage />
      </>
    );
  }

  if (view === VIEWS.payments) {
    return (
      <>
        <PageHeader
          eyebrow="FINANCE"
          title="Paiements"
          description="Enregistrez les encaissements, suivez les paiements partiels et le crédit des familles."
        />

        <button type="button" className="finance-back-button" onClick={goHome}>
          <ArrowLeft size={17} />
          Retour à Finance & caisse
        </button>

        <PaymentsPanel />
      </>
    );
  }

  if (view === VIEWS.cash && (canGlobalFinance || canOwnCash)) {
    return (
      <>
        <PageHeader
          eyebrow="FINANCE"
          title="Caisse"
          description={canGlobalFinance
            ? 'Contrôlez les espèces détenues par les agents, les remises et les écarts de caisse.'
            : 'Suivez vos encaissements en espèces et soumettez votre vide de caisse au gérant.'}
        />

        <button type="button" className="finance-back-button" onClick={goHome}>
          <ArrowLeft size={17} />
          Retour à Finance & caisse
        </button>

        <CashPage />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="MODULE"
        title={(canGlobalFinance || canContracts) ? title : 'Paiements élèves'}
        description={canGlobalFinance
          ? 'Choisissez l’espace financier que vous souhaitez ouvrir.'
          : canContracts
            ? 'Accès administratif : enregistrez les paiements et gérez les contrats des élèves.'
            : 'Accès ciblé : recherchez un élève précis pour consulter et enregistrer ses paiements.'}
      />

      <section className="finance-hub-grid">
        {canStudentPayments && <button
          type="button"
          className="finance-hub-card"
          onClick={() => openView(VIEWS.payments)}
        >
          <span className="finance-hub-icon">
            <Banknote size={26} />
          </span>
          <div>
            <h2>Enregistrer un paiement</h2>
            <p>
              Paiement total, partiel, avance famille, crédit et historique.
            </p>
          </div>
          <span className="finance-hub-action">
            Ouvrir les paiements
          </span>
        </button>}

        {canContracts && <button
          type="button"
          className="finance-hub-card"
          onClick={() => openView(VIEWS.contracts)}
        >
          <span className="finance-hub-icon">
            <FileSignature size={26} />
          </span>
          <div>
            <h2>Voir les contrats</h2>
            <p>
              Créez les contrats, suivez les validations et les dossiers incomplets.
            </p>
          </div>
          <span className="finance-hub-action">
            Ouvrir les contrats
          </span>
        </button>}

        {(canGlobalFinance || canOwnCash) && <button
          type="button"
          className="finance-hub-card"
          onClick={() => openView(VIEWS.cash)}
        >
          <span className="finance-hub-icon">
            <Landmark size={26} />
          </span>
          <div>
            <h2>Voir la caisse</h2>
            <p>
              Encaissements du jour, sorties, clôture et contrôle de caisse.
            </p>
          </div>
          <span className="finance-hub-action">
            Ouvrir la caisse
          </span>
        </button>}
      </section>

    </>
  );
}